import { useCallback } from 'react';
import CameraIcon from '@/assets/camera.svg';
import InfoIcon from '@/assets/info.svg';
import MicIcon from '@/assets/mic.svg';
import WarningIcon from '@/assets/warning.svg';
import type { MediaErrorInfo } from '@/hooks/get-user-media-error-info';
import { MediaDeviceInfo } from '@/hooks/use-media-devices';
import DeviceSelector from './device-selector';
import MediaErrorOverlay from './media-error-overlay';

interface DeviceSelectionPanelProps {
  /**
   * Holds the list of available video and audio devices
   */
  videoDevices: MediaDeviceInfo[];
  audioDevices: MediaDeviceInfo[];
  selectedVideoDevice: string | null;
  selectedAudioDevice: string | null;

  /**
   * Callbacks for selecting devices and enumerating available devices
   */
  onSelectVideoDevice: (deviceId: string) => void;
  onSelectAudioDevice: (deviceId: string) => void;
  onEnumerateDevices: () => Promise<void>;

  /**
   * State flags for device enumeration and permission status
   */
  isEnumerating: boolean;
  hasDevicePermission: boolean;
  deviceError: MediaErrorInfo | null;

  /**
   * Flag to indicate if a call is currently active
   */
  isCallActive?: boolean;
}

/**
 * Device selection panel component for WebRTC calling
 * Provides video and audio device selection with error handling
 */
export default function DeviceSelectionPanel({
  videoDevices,
  audioDevices,
  selectedVideoDevice,
  selectedAudioDevice,
  onSelectVideoDevice,
  onSelectAudioDevice,
  onEnumerateDevices,
  isEnumerating,
  hasDevicePermission,
  deviceError,
  isCallActive = false,
}: DeviceSelectionPanelProps) {
  const handleRetryDeviceAccess = useCallback(async () => {
    await onEnumerateDevices();
  }, [onEnumerateDevices]);

  /**
   * Show loading state while devices are being enumerated (i.e. waiting
   * for user permission to access camera/microphone, then getting list)
   */
  if (isEnumerating) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
          <span className="text-sm text-gray-600">Detecting devices...</span>
        </div>
      </div>
    );
  }

  /**
   * If there was an error accessing devices, show the shared error
   * overlay with advice and a retry button that re-requests permissions.
   */
  if (deviceError) {
    return (
      <MediaErrorOverlay
        error={deviceError}
        onRetry={handleRetryDeviceAccess}
      />
    );
  }

  /**
   * Once enumerated devices, show the device selection panel
   */
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DeviceSelector
          devices={videoDevices}
          icon={<CameraIcon className="h-4 w-4 text-gray-500" />}
          isDisabled={isCallActive}
          label="Camera"
          onSelectDevice={onSelectVideoDevice}
          selectedDevice={selectedVideoDevice}
        />

        <DeviceSelector
          devices={audioDevices}
          icon={<MicIcon className="h-4 w-4 text-gray-500" />}
          isDisabled={isCallActive}
          label="Microphone"
          onSelectDevice={onSelectAudioDevice}
          selectedDevice={selectedAudioDevice}
        />
      </div>

      {isCallActive && (
        <div className="mt-3 rounded-md bg-blue-50 p-3">
          <div className="flex items-center gap-2">
            <InfoIcon className="h-4 w-4 text-blue-600" />
            <span className="text-xs text-blue-800">
              It's not possible to change microphone or camera during calls -
              make sure you have the right one connected before ringing.
            </span>
          </div>
        </div>
      )}

      {!hasDevicePermission && (
        <div className="mt-3 rounded-md bg-yellow-50 p-3">
          <div className="flex items-center gap-2">
            <WarningIcon className="h-4 w-4 text-yellow-600" />
            <span className="text-xs text-yellow-800">
              Camera and microphone access is required for calls
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
